import {
  ArrowRightIcon,
  CheckIcon,
  Divider,
  GKButton,
  GKModal,
  GKTextField,
  Grid2,
  RupeeIcon,
  Typography,
  WalletIcon1,
} from "components";
import { useMutation } from "hooks/useQueries";
import { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { moneyCommaSeparator } from "utils";
import { moneyCommaSeparatorPoint } from "utils/MathFunction";
import axiosInstance from "utils/axiosInstance";
import { GKModalProps } from "utils/commonInterface";
import { refetchQuery } from "utils/helpers";

interface FormValues {
  amount: number;
}

const WalletTopupModal = (props: GKModalProps) => {
  const { open, setOpen, data } = props;

  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);

  const { mutate: handleWalletTopup, isLoading } = useMutation({
    mutationFn: (data) => axiosInstance.post(`/wallet-top-up/`, data),
    onSuccess: (response) => {
      refetchQuery("GET_WALLET_BALANCE");
      setOpen(false);

      const paymentUrl = response.data?.data?.link;

      if (paymentUrl && typeof paymentUrl === "string") {
        const link = document.createElement("a");
        link.href = paymentUrl;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },
  });

  const form = useForm<FormValues>({
    defaultValues: {
      amount: 0,
    },
  });

  const calculationData: any = useMemo(() => {
    const amount = Number(form.watch("amount")) || 0;

    const GST_PERCENTAGE = {
      CGST: 9,
      SGST: 9,
      IGST: 18,
    };

    const isSameState = data?.data?.data?.state?.toLowerCase() === "gujarat";

    const taxData = {
      cgst: 0,
      sgst: 0,
      igst: 0,
      totalAmount: amount,
      isSameState,
    };

    if (taxData.isSameState) {
      taxData.cgst = Number(((amount * GST_PERCENTAGE.CGST) / 100).toFixed(2));
      taxData.sgst = Number(((amount * GST_PERCENTAGE.SGST) / 100).toFixed(2));
    } else {
      taxData.igst = Number(((amount * GST_PERCENTAGE.IGST) / 100).toFixed(2));
    }

    const netAmount = amount + taxData.cgst + taxData.sgst + taxData.igst;

    return {
      ...taxData,
      netAmount: Number(netAmount.toFixed(2)),
    };
  }, [form.watch("amount"), data]);

  return (
    <GKModal
      open={open}
      setOpen={setOpen}
      width={"400px"}
      modalTitle={"Wallet Topup"}
      subTitle={
        <>
          <Grid2 size={12} display="flex" alignItems="center" gap={1} mt={0.5}>
            <WalletIcon1 />
            <Typography
              fontSize={12}
              fontWeight={400}
              color="var(--neutral-500)"
            >
              Current balance:
            </Typography>
            <Typography fontSize={12} fontWeight={500} color="var(--grey-900)">
              {moneyCommaSeparator(data?.data?.data?.amount)}
            </Typography>
          </Grid2>
        </>
      }
      footer={
        <Grid2 container justifyContent={"flex-end"} gap={1}>
          <GKButton
            style={{ width: "180px" }}
            variant="outlined"
            size="medium"
            onClick={() => setOpen(false)}
          >
            Cancel
          </GKButton>
          <GKButton
            variant="contained"
            style={{ width: "180px" }}
            type="submit"
            form="wallet-topup-form"
            size="medium"
            loading={isLoading}
            endIcon={<ArrowRightIcon />}
            disabled={
              !form.watch("amount") || Number(form.watch("amount")) <= 0
            }
          >
            Continue to Payment
          </GKButton>
        </Grid2>
      }
    >
      <form
        id="wallet-topup-form"
        onSubmit={form.handleSubmit((values: any) => handleWalletTopup(values))}
      >
        <Grid2 container spacing={1}>
          <Grid2 size={12}>
            <Typography
              fontSize={13}
              fontWeight={500}
              color="var(--grey-900)"
              mt={1.5}
              mb={0.5}
            >
              Quick Add
            </Typography>
            <div style={{ display: "flex", flexWrap: "nowrap" }}>
              {[1000, 2000, 5000, 10000].map((amount) => (
                <GKButton
                  key={amount}
                  startIcon={
                    selectedAmount === amount ? <CheckIcon /> : undefined
                  }
                  onClick={() => {
                    setSelectedAmount(amount);
                    form.setValue("amount", amount);
                  }}
                  style={{
                    border:
                      selectedAmount === amount
                        ? "1px solid var(--primary-200)"
                        : "1px solid var(--neutral-100)",
                    marginRight: "8px",
                    width: "90px",
                    color:
                      selectedAmount === amount
                        ? "var(--primary-900)"
                        : "#0B0D12",
                    backgroundColor:
                      selectedAmount === amount
                        ? "var(--primary-100)"
                        : "transparent",
                  }}
                >
                  ₹{amount.toLocaleString("en-IN")}
                </GKButton>
              ))}
            </div>
          </Grid2>
          <Grid2 size={12} mt={1.5}>
            <Controller
              name="amount"
              control={form.control}
              render={({ field }) => (
                <GKTextField
                  {...field}
                  inputLabel={"Amount"}
                  type="text"
                  value={moneyCommaSeparatorPoint(field.value)}
                  onChange={(e) => {
                    const numericValue = e.target.value.replace(/,/g, "");
                    const limitedValue = Math.min(Number(numericValue), 10000);
                    field.onChange(limitedValue);
                  }}
                  slotProps={{
                    input: {
                      endAdornment: <RupeeIcon />,
                    },
                  }}
                />
              )}
            />
          </Grid2>
          <Grid2 size={12}>
            <Typography fontSize={12} fontWeight={500} color="var(--grey-500)">
              Maximum single top-up limit is ₹10,000
            </Typography>
          </Grid2>

          <Grid2
            size={12}
            p={1.5}
            mt={1.5}
            bgcolor="var(--grey-bg-100)"
            borderRadius={"8px"}
          >
            <Grid2
              size={7}
              marginLeft={"auto"}
              display={"flex"}
              justifyContent={"space-between"}
              alignItems={"flex-end"}
              gap={1}
              mb={1}
            >
              <Typography
                fontSize={12}
                fontWeight={400}
                color="var(--neutral-500)"
              >
                Total:
              </Typography>
              <Typography
                fontSize={12}
                fontWeight={400}
                color="var(--grey-900)"
              >
                ₹{moneyCommaSeparator(calculationData?.totalAmount) || "0.00"}
              </Typography>
            </Grid2>
            {calculationData?.isSameState ? (
              <>
                <Grid2
                  size={7}
                  marginLeft={"auto"}
                  display={"flex"}
                  justifyContent={"space-between"}
                  alignItems={"flex-end"}
                  gap={1}
                  mb={1}
                >
                  <Typography
                    fontSize={12}
                    fontWeight={400}
                    color="var(--neutral-500)"
                  >
                    CGST (9%):
                  </Typography>
                  <Typography
                    fontSize={12}
                    fontWeight={400}
                    color="var(--grey-900)"
                  >
                    ₹{calculationData?.cgst?.toFixed(2) || "0.00"}
                  </Typography>
                </Grid2>

                <Grid2
                  size={7}
                  marginLeft={"auto"}
                  display={"flex"}
                  justifyContent={"space-between"}
                  alignItems={"flex-end"}
                  gap={1}
                  mb={1}
                >
                  <Typography
                    fontSize={12}
                    fontWeight={400}
                    color="var(--neutral-500)"
                  >
                    SGST (9%):
                  </Typography>
                  <Typography
                    fontSize={12}
                    fontWeight={400}
                    color="var(--grey-900)"
                  >
                    ₹{calculationData?.sgst?.toFixed(2) || "0.00"}
                  </Typography>
                </Grid2>
              </>
            ) : (
              <Grid2
                size={7}
                marginLeft={"auto"}
                display={"flex"}
                justifyContent={"space-between"}
                alignItems={"flex-end"}
                gap={1}
                mb={1}
              >
                <Typography
                  fontSize={12}
                  fontWeight={400}
                  color="var(--neutral-500)"
                >
                  IGST (18%):
                </Typography>
                <Typography
                  fontSize={12}
                  fontWeight={400}
                  color="var(--grey-900)"
                >
                  ₹{calculationData?.igst?.toFixed(2) || "0.00"}
                </Typography>
              </Grid2>
            )}
            <Divider
              style={{ width: "60%", marginLeft: "auto", marginBottom: "8px" }}
            />
            <Grid2
              size={7}
              marginLeft={"auto"}
              display={"flex"}
              justifyContent={"space-between"}
              alignItems={"flex-end"}
              gap={1}
            >
              <Typography
                fontSize={12}
                fontWeight={400}
                color="var(--neutral-500)"
              >
                Grand Total
              </Typography>
              <Typography
                fontSize={14}
                fontWeight={700}
                color="var(--black-900)"
              >
                ₹{moneyCommaSeparator(calculationData?.netAmount) || "0.00"}
              </Typography>
            </Grid2>
          </Grid2>
        </Grid2>
      </form>
    </GKModal>
  );
};

export default WalletTopupModal;
